/**
 * Team Settings Page
 *
 * Admin/Manager interface for managing organization invitations.
 * Features role badges, status indicators, and invitation management.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  UserPlus,
  AlertCircle,
  Loader2,
  Mail,
  Clock,
  X,
  ChevronDown,
  Send,
  XCircle,
  Check,
  Ban,
  Shield,
  Trash2,
} from 'lucide-react';
import {
  useInvitations,
  useCreateInvitation,
  useRevokeInvitation,
} from '../../hooks/useInvitations';
import { useCurrentUser } from '../../hooks/useUsers';
import type { InvitationListItem, InvitationStatus, UserRole } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

// Role configuration with colors and labels
const roleConfig: Partial<Record<UserRole, { label: string; bg: string; text: string; dot: string }>> = {
  super_admin: {
    label: 'Super Admin',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
  },
  admin: {
    label: 'Admin',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500',
  },
  manager: {
    label: 'Manager',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    dot: 'bg-violet-500',
  },
  trainer: {
    label: 'Trainer',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
  },
  operator: {
    label: 'Operator',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
};

// Fallback for any legacy roles not in roleConfig
const defaultRoleStyle = { label: 'Unknown', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };

// Status configuration
const statusConfig: Record<InvitationStatus, { label: string; bg: string; text: string; dot: string; icon: typeof Check }> = {
  pending: {
    label: 'Pending',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
    icon: Clock,
  },
  accepted: {
    label: 'Accepted',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    icon: Check,
  },
  expired: {
    label: 'Expired',
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    dot: 'bg-gray-400',
    icon: Clock,
  },
  revoked: {
    label: 'Revoked',
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
    icon: Ban,
  },
};

const roleOptions: UserRole[] = ['super_admin', 'admin', 'manager', 'trainer', 'operator'];

export function TeamPage() {
  useDocumentTitle('Team');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const canManageInvitations = isSuperAdmin || userRole === 'admin' || userRole === 'manager';

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: invitationsResponse, isLoading, error } = useInvitations();
  const revokeMutation = useRevokeInvitation();

  // Extract invitations array from paginated response
  const invitations = Array.isArray(invitationsResponse)
    ? invitationsResponse
    : invitationsResponse?.data ?? [];

  const handleRevoke = async (id: string, email: string) => {
    if (window.confirm(`Are you sure you want to revoke the invitation for ${email}?`)) {
      await revokeMutation.mutateAsync(id);
    }
  };

  // TODO: Wire up to a backend DELETE endpoint for revoked invitations once implemented.
  // Currently no endpoint supports hard-deleting revoked/expired invitations.
  const handleDelete = async (_id: string, email: string) => {
    if (window.confirm(`Are you sure you want to remove the revoked invitation for ${email}?`)) {
      window.alert('Deletion of revoked invitations is not yet supported. This feature requires a backend endpoint.');
    }
  };

  // Unauthorized access
  if (!canManageInvitations) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 mb-5">
          <Shield size={32} className="text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Access Restricted</h3>
        <p className="mt-2 text-sm text-gray-500 max-w-sm text-center">
          You don't have permission to manage team invitations. Contact your administrator for access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-guild-mint/50">
            <UserPlus size={24} className="text-guild-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Team
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Manage team invitations and access
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="
            inline-flex items-center gap-2 px-5 py-2.5
            text-sm font-semibold text-white bg-guild-green
            rounded-xl shadow-lg shadow-guild-green/20
            hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
            active:scale-[0.98]
            transition-all duration-200
          "
        >
          <UserPlus size={18} />
          Invite Member
        </button>
      </div>

      {/* Content */}
      {error ? (
        <ErrorState />
      ) : isLoading ? (
        <LoadingState />
      ) : invitations.length > 0 ? (
        <InvitationTable
          invitations={invitations}
          onRevoke={handleRevoke}
          onDelete={handleDelete}
          isRevoking={revokeMutation.isPending}
          isDeleting={false}
        />
      ) : (
        <EmptyState onInvite={() => setIsModalOpen(true)} />
      )}

      {/* Invite Modal */}
      {isModalOpen && (
        <InviteModal onClose={() => setIsModalOpen(false)} />
      )}
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

interface InvitationTableProps {
  invitations: InvitationListItem[];
  onRevoke: (id: string, email: string) => void;
  onDelete: (id: string, email: string) => void;
  isRevoking: boolean;
  isDeleting: boolean;
}

function InvitationTable({ invitations, onRevoke, onDelete, isRevoking, isDeleting }: InvitationTableProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Invited By
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Expires
            </th>
            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {invitations.map((invitation) => (
            <InvitationRow
              key={invitation.id}
              invitation={invitation}
              onRevoke={() => onRevoke(invitation.id, invitation.email)}
              onDelete={() => onDelete(invitation.id, invitation.email)}
              isRevoking={isRevoking}
              isDeleting={isDeleting}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface InvitationRowProps {
  invitation: InvitationListItem;
  onRevoke: () => void;
  onDelete: () => void;
  isRevoking: boolean;
  isDeleting: boolean;
}

function InvitationRow({ invitation, onRevoke, onDelete, isRevoking, isDeleting }: InvitationRowProps) {
  const role = roleConfig[invitation.role as keyof typeof roleConfig] || defaultRoleStyle;
  const status = statusConfig[invitation.status as InvitationStatus] || statusConfig.pending;

  const formatExpiry = (date: Date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMs < 0) return 'Expired';
    if (diffHours < 1) return 'Less than 1 hour';
    if (diffHours < 24) return `${diffHours} hours`;
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const isPending = invitation.status === 'pending';
  const isExpired = new Date(invitation.expiresAt) < new Date();

  return (
    <tr className="group hover:bg-gray-50/50 transition-colors">
      {/* Email */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
            <Mail size={18} className="text-gray-400" />
          </div>
          <span className="text-sm font-medium text-gray-900">
            {invitation.email}
          </span>
        </div>
      </td>

      {/* Role Badge */}
      <td className="px-6 py-4">
        <span
          className={`
            inline-flex items-center gap-1.5 rounded-full px-2.5 py-1
            text-xs font-semibold
            ${role.bg} ${role.text}
          `}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${role.dot}`} />
          {role.label}
        </span>
      </td>

      {/* Status Badge */}
      <td className="px-6 py-4">
        <span
          className={`
            inline-flex items-center gap-1.5 rounded-full px-2.5 py-1
            text-xs font-semibold
            ${status.bg} ${status.text}
          `}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
      </td>

      {/* Invited By */}
      <td className="px-6 py-4">
        <span className="text-sm text-gray-600">
          {invitation.invitedBy?.fullName || 'Unknown'}
        </span>
      </td>

      {/* Expires */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <Clock
            size={14}
            className={isExpired || !isPending ? 'text-gray-300' : 'text-gray-400'}
          />
          <span
            className={`
              text-sm
              ${isExpired || !isPending ? 'text-gray-400' : 'text-gray-600'}
            `}
          >
            {isPending ? formatExpiry(invitation.expiresAt) : '—'}
          </span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-1">
          {isPending && !isExpired && (
            <button
              onClick={onRevoke}
              disabled={isRevoking}
              className="
                inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                text-sm font-medium text-red-600
                hover:bg-red-50 hover:text-red-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              <XCircle size={14} />
              Revoke
            </button>
          )}
          {invitation.status === 'revoked' && (
            // TODO: Backend DELETE endpoint for revoked invitations not yet implemented.
            // Current DELETE /api/invitations/:id only handles pending invitations (revokes them).
            // Need a new endpoint or modify existing to allow hard-deleting revoked/expired invitations.
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className="
                inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                text-sm font-medium text-gray-500
                hover:bg-gray-100 hover:text-gray-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              <Trash2 size={14} />
              Remove
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

interface InviteModalProps {
  onClose: () => void;
}

function InviteModal({ onClose }: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('operator');
  const [error, setError] = useState<string | null>(null);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);

  const createMutation = useCreateInvitation();
  const isPending = createMutation.isPending;

  // ESC to close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) {
        onClose();
      }
    },
    [onClose, isPending]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsRoleDropdownOpen(false);
    if (isRoleDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isRoleDropdownOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await createMutation.mutateAsync({ email: email.trim().toLowerCase(), role });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    }
  };

  const currentRoleConfig = roleConfig[role] || defaultRoleStyle;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={!isPending ? onClose : undefined}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-mint/50">
              <UserPlus size={20} className="text-guild-green" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Invite Member</h2>
              <p className="text-xs text-gray-500">Send an invitation to join your team</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isPending}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Role Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Role
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRoleDropdownOpen(!isRoleDropdownOpen);
                  }}
                  disabled={isPending}
                  className="
                    w-full flex items-center justify-between
                    rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm text-left
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                >
                  <span
                    className={`
                      inline-flex items-center gap-1.5 rounded-full px-2.5 py-1
                      text-xs font-semibold
                      ${currentRoleConfig.bg} ${currentRoleConfig.text}
                    `}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${currentRoleConfig.dot}`} />
                    {currentRoleConfig.label}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Dropdown */}
                {isRoleDropdownOpen && (
                  <div className="absolute z-10 mt-2 w-full rounded-xl border border-gray-100 bg-white shadow-xl overflow-hidden">
                    {roleOptions.map((roleOption) => {
                      const config = roleConfig[roleOption]!;
                      const isSelected = roleOption === role;
                      return (
                        <button
                          key={roleOption}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRole(roleOption);
                            setIsRoleDropdownOpen(false);
                          }}
                          className={`
                            w-full flex items-center justify-between px-4 py-3 text-left
                            hover:bg-gray-50 transition-colors
                            ${isSelected ? 'bg-gray-50' : ''}
                          `}
                        >
                          <span
                            className={`
                              inline-flex items-center gap-1.5 rounded-full px-2.5 py-1
                              text-xs font-semibold
                              ${config.bg} ${config.text}
                            `}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
                            {config.label}
                          </span>
                          {isSelected && (
                            <Check size={16} className="text-guild-green" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
              Send Invitation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Loader2 size={32} className="text-guild-green animate-spin" />
      <p className="mt-4 text-sm text-gray-500">Loading invitations...</p>
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
      <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
      <div>
        <p className="text-sm font-medium text-red-800">Failed to load invitations</p>
        <p className="text-sm text-red-600">
          Please try refreshing the page or contact support if the issue persists.
        </p>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  onInvite: () => void;
}

function EmptyState({ onInvite }: EmptyStateProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-guild-mint/30 mb-5">
          <UserPlus size={32} className="text-guild-green/60" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          No invitations yet
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          Get started by inviting team members to join your organization.
        </p>

        <button
          onClick={onInvite}
          className="
            mt-6 inline-flex items-center gap-2 px-5 py-2.5
            text-sm font-semibold text-white bg-guild-green
            rounded-xl shadow-lg shadow-guild-green/20
            hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
            active:scale-[0.98]
            transition-all duration-200
          "
        >
          <UserPlus size={18} />
          Invite Your First Member
        </button>
      </div>
    </div>
  );
}
